import styled from '@emotion/styled';
import { Button, Text } from '@innovationdepartment/proxima-ui';
import type { Brand } from 'types/stores/brand-store';
import SectionLayout from 'ui/SectionLayout';
import type { PersonaFileData } from './BrandPersonasForm.types';
import DataPreview from './DataPreview';

const FormContainer = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const FileUploadContainer = styled.div`
  border: 2px dashed #ccc;
  border-radius: 8px;
  padding: 40px 20px;
  text-align: center;
  background-color: #fafafa;
  transition: border-color 0.2s ease;

  &:hover {
    border-color: #007bff;
  }

  &.drag-over {
    border-color: #007bff;
    background-color: #f0f8ff;
  }
`;

const FileInput = styled.input`
  display: none;
`;

const UploadText = styled.div`
  margin-bottom: 16px;
`;

const ErrorMessage = styled.div`
  margin-top: 16px;
  padding: 12px;
  background-color: #ffebee;
  border-radius: 6px;
  border: 1px solid #f44336;
  color: #c62828;
`;

const SuccessMessage = styled.div`
  margin-top: 16px;
  padding: 12px;
  background-color: #e8f5e8;
  border-radius: 6px;
  border: 1px solid #4caf50;
  color: #2e7d32;
`;

const DataPreviewContainer = styled.div`
  display: flex;
  gap: 20px;
  margin-top: 20px;

  > * {
    flex: 1;
    min-width: 0;
  }
`;

type CrossBrandPurchase = {
  purchaseType?: string;
  data?: {
    top_100_other_brand_products?: unknown[];
    top_100_other_brand_tags?: unknown[];
  };
};

type BrandPersonasFormViewProps = {
  handleDragOver: (e: React.DragEvent<HTMLDivElement>) => void;
  handleDragLeave: (e: React.DragEvent<HTMLDivElement>) => void;
  handleDrop: (e: React.DragEvent<HTMLDivElement>) => void;
  handleFileInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  fileData: PersonaFileData | null;
  dragOver: boolean;
  error: string | null;
  success: string | null;
  loading: boolean;
  personas: { data: unknown };
  personaCrossBrandPurchases: unknown[];
  brand: Brand;
};

const BrandPersonasFormView = ({
  handleDragOver,
  handleDragLeave,
  handleDrop,
  handleFileInputChange,
  fileData,
  dragOver,
  error,
  success,
  loading,
  personas,
  personaCrossBrandPurchases,
  brand
}: BrandPersonasFormViewProps) => {
  const crossBrandPurchases = personaCrossBrandPurchases as CrossBrandPurchase[] | undefined;

  const personaCrossBrandPurchasesOtherProducts = crossBrandPurchases?.find(
    item => item.purchaseType === 'others_products'
  );
  const crossBrandPurchaseTopBrands = crossBrandPurchases?.find(
    item => item.purchaseType === 'top_brands'
  );

  const brandProducts =
    personaCrossBrandPurchasesOtherProducts?.data?.top_100_other_brand_products || [];

  const topBrands = crossBrandPurchaseTopBrands?.data?.top_100_other_brand_tags || [];

  return (
    <SectionLayout title={`Brand Personas - (${brand.name})`}>
      <FormContainer>
        <div>
          <Text variant="body1" color="neutral70">
            Upload JSON File
          </Text>
          <Text variant="body2" color="neutral60">
            Select or drag and drop a JSON file containing your brand personas data.
          </Text>
        </div>

        <FileUploadContainer
          className={dragOver ? 'drag-over' : ''}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <FileInput
            type="file"
            accept=".json,application/json"
            onChange={handleFileInputChange}
            id="file-upload"
          />

          <UploadText>
            <Text variant="body1" color="neutral70">
              {fileData?.content
                ? 'File selected successfully!'
                : 'Drag & drop your JSON file here'}
            </Text>
            <Text variant="body2" color="neutral60">
              or click to browse
            </Text>
          </UploadText>

          <Button
            variant="outlined"
            label="Choose File"
            onClick={() => document.getElementById('file-upload')?.click()}
            disabled={loading}
          />
        </FileUploadContainer>

        {!!error && (
          <ErrorMessage>
            <Text variant="body2Medium">{error}</Text>
          </ErrorMessage>
        )}

        {!!success && (
          <SuccessMessage>
            <Text variant="body1">{success}</Text>
          </SuccessMessage>
        )}
      </FormContainer>
      <DataPreviewContainer>
        <DataPreview data={personas?.data} title="Current Brand Personas" />
        <DataPreview data={brandProducts} title="Other Products Purchased" />
        <DataPreview data={topBrands} title="Top Brand Types" />
      </DataPreviewContainer>
    </SectionLayout>
  );
};

export default BrandPersonasFormView;
